import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import {
  Upload,
  RefreshCw,
  ExternalLink,
  History,
  ArrowLeft,
} from "lucide-react";

const button =
  "px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium disabled:opacity-40";
const statusClass = (status) =>
  status === "failed"
    ? "text-red-700 bg-red-50"
    : ["created", "updated"].includes(status)
    ? "text-emerald-700 bg-emerald-50"
    : "text-slate-600 bg-slate-100";
const message = (e) =>
  e.response?.data?.error || e.message || "Unable to load AI themes.";
const display = (value) => (value == null ? "—" : String(value));

export default function AIThemes() {
  const api = useContext(APIContext);
  const [file, setFile] = useState(null),
    [uploading, setUploading] = useState(false);
  const [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [loadError, setLoadError] = useState("");
  const [imports, setImports] = useState([]),
    [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]),
    [counts, setCounts] = useState([]),
    [rowOffset, setRowOffset] = useState(0);
  const [themes, setThemes] = useState([]),
    [total, setTotal] = useState(0),
    [offset, setOffset] = useState(0);
  const [search, setSearch] = useState(""),
    [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const request = useRef(null);

  useEffect(() => {
    let cancelled = false,
      timer;
    const refresh = async () => {
      try {
        const [catalog, jobs, detail] = await Promise.all([
          api.getAiThemes(search, offset),
          api.getAiThemeImports(),
          selected
            ? api.getAiThemeImport(selected, rowOffset)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setThemes(catalog.themes);
        setTotal(catalog.total);
        setImports(jobs);
        if (detail) {
          setRows(detail.rows);
          setCounts(detail.counts);
        }
        setLoadError("");
      } catch (e) {
        if (!cancelled) setLoadError(message(e));
      } finally {
        if (!cancelled) {
          setLoading(false);
          timer = setTimeout(refresh, 4000);
        }
      }
    };
    refresh();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, search, offset, selected, rowOffset, notice]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || uploading) return;
    setUploading(true);
    setError("");
    setNotice("");
    request.current = request.current || crypto.randomUUID();
    try {
      const result = await api.uploadAiThemes(file, request.current);
      setSelected(result.id);
      setRowOffset(0);
      setNotice(
        `Import #${result.id} queued: ${result.rows} row${
          result.rows === 1 ? "" : "s"
        }. You can leave this page while it runs.`
      );
      request.current = null;
    } catch (e) {
      setError(message(e));
    } finally {
      setUploading(false);
    }
  };
  const showHistory = async (theme) => {
    try {
      setHistory({ theme, entries: await api.getAiThemeHistory(theme.id) });
    } catch (e) {
      setError(message(e));
    }
  };
  const rowTotal = counts.reduce((sum, c) => sum + c.count, 0);
  return (
    <div className="space-y-6 pb-12">
      <HelmetLayout title="AI themes" />
      <Link
        to="/themes"
        className="inline-flex items-center gap-2 text-sm text-slate-500"
      >
        <ArrowLeft size={16} /> All themes
      </Link>
      <header>
        <h1 className="text-2xl font-bold text-slate-900">AI themes</h1>
        <p className="text-slate-500 mt-1">
          One shared sheet. Every template, kept in sync.
        </p>
      </header>
      <form
        onSubmit={upload}
        className="bg-white rounded-xl border border-slate-200 p-6 space-y-4"
      >
        <div>
          <h2 className="font-semibold text-slate-900">
            Import your team's Excel or CSV file
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Use WebsiteBuilderCategory, Name, Slug, and ProjectID. Priority is
            optional: higher numbers appear first; blank or missing values use 0.
            Only Release=Yes rows are imported when Release is present. Excel
            uses the first worksheet. Other extra columns are ignored. Rows with
            missing or invalid slugs are skipped. Upload again whenever the sheet
            or websites change.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex-1 min-w-60 border border-dashed border-slate-300 rounded-lg p-4 text-sm">
            <span className="block font-medium mb-2">Template Excel or CSV</span>
            <input
              aria-label="Template Excel or CSV"
              type="file"
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={uploading}
              onChange={(e) => {
                setFile(e.target.files[0] || null);
                request.current = null;
              }}
            />
          </label>
          <button
            type="submit"
            disabled={!file || uploading}
            className="bg-[#623ce7] text-white rounded-lg px-5 py-3 flex items-center gap-2 disabled:opacity-40"
          >
            <Upload size={17} />
            {uploading ? "Uploading…" : "Import templates"}
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Unchanged themes are skipped. Names, slugs, categories, and priorities
          follow the sheet. Missing rows do not delete existing themes.
          Screenshots are generated automatically.
        </p>
      </form>
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 text-red-800 p-4">
          {error}
        </div>
      )}
      {loadError && (
        <div role="alert" className="rounded-lg bg-red-50 text-red-800 p-4">
          {loadError}
        </div>
      )}
      {notice && (
        <div
          role="status"
          className="rounded-lg bg-violet-50 text-violet-800 p-4"
        >
          {notice}
        </div>
      )}
      <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Recent imports</h2>
          <span className="text-xs text-slate-500 flex gap-2 items-center">
            <RefreshCw size={12} /> Updates automatically
          </span>
        </div>
        {!imports.length && (
          <p className="text-sm text-slate-500">No imports yet.</p>
        )}
        <div className="flex flex-wrap gap-2">
          {imports.map((job) => (
            <button
              key={job.id}
              onClick={() => {
                setSelected(job.id);
                setRowOffset(0);
              }}
              className={`${button} text-left ${
                selected === job.id ? "ring-2 ring-violet-400" : ""
              }`}
            >
              <span className="block">
                #{job.id} · {job.filename}
              </span>
              <span className="text-xs text-slate-500">
                {Object.entries(job.counts)
                  .map(([s, n]) => `${n} ${s}`)
                  .join(" · ")}
              </span>
            </button>
          ))}
        </div>
        {selected && (
          <>
            <h3 className="text-sm font-medium">Import #{selected}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-slate-500 border-b">
                  <tr>
                    {["Row", "Template", "Priority", "Status", "Result"].map((x) => (
                      <th key={x} className="p-3">
                        {x}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100">
                      <td className="p-3">{row.row_number}</td>
                      <td className="p-3">
                        <div>{row.name}</div>
                        <div className="text-xs text-slate-500">{row.slug}</div>
                      </td>
                      <td className="p-3">{row.priority ?? 0}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${statusClass(
                            row.status
                          )}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="p-3 text-xs max-w-lg break-words">
                        {row.error ||
                          Object.entries(row.changes).map(([k, v]) => (
                            <div key={k}>
                              {k}: {display(v.before)} → {display(v.after)}
                            </div>
                          ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-3 items-center text-xs">
              <button
                className={button}
                disabled={!rowOffset}
                onClick={() => setRowOffset(Math.max(0, rowOffset - 100))}
              >
                Previous rows
              </button>
              <span>
                {rowTotal ? rowOffset + 1 : 0}–
                {Math.min(rowOffset + 100, rowTotal)} of {rowTotal}
              </span>
              <button
                className={button}
                disabled={rowOffset + 100 >= rowTotal}
                onClick={() => setRowOffset(rowOffset + 100)}
              >
                Next rows
              </button>
            </div>
            {counts.some((c) => c.status === "failed") && (
              <p className="text-xs text-red-700">
                Correct failed rows and upload the CSV again. Successful,
                unchanged rows will be skipped.
              </p>
            )}
            {counts.some(
              (c) => c.status === "queued" || c.status === "processing"
            ) && (
              <p className="text-xs text-slate-500">
                Waiting for the import worker. Building and capturing each new
                website can take a few minutes.
              </p>
            )}
          </>
        )}
      </section>
      <section className="space-y-4">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <h2 className="font-semibold">
            Theme registry <span className="text-slate-400">({total})</span>
          </h2>
          <input
            aria-label="Search AI themes"
            placeholder="Search name, slug, category, or project ID"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
            className="border rounded-lg px-3 py-2 text-sm w-80"
          />
        </div>
        {loading && <p className="text-slate-500 text-sm">Loading themes…</p>}
        {!loading && !themes.length && (
          <p className="text-slate-500 text-sm">
            Successfully imported themes will appear here.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {themes.map((theme) => (
            <article
              key={theme.id}
              className="bg-white border border-slate-200 rounded-xl overflow-hidden"
            >
              <img
                className="w-full aspect-[1.44] object-cover object-top bg-slate-100"
                src={api.aiThemeThumbnailUrl(theme)}
                alt={`${theme.name} preview`}
                loading="lazy"
              />
              <div className="p-4 space-y-2">
                <span className="text-xs text-violet-700">
                  {theme.category}
                </span>
                <h3 className="font-semibold">{theme.name}</h3>
                <p className="text-sm text-slate-500">/{theme.slug}</p>
                <p className="text-sm text-slate-500">
                  Priority: {theme.priority ?? 0}
                </p>
                <p className="text-xs text-slate-400 break-all">
                  Project: {theme.source_project_id}
                </p>
                <p className="text-xs text-slate-400 break-all">
                  Version: {theme.source_version}
                </p>
                <div className="flex gap-4 pt-2 text-sm">
                  <a
                    className="inline-flex gap-1 items-center text-violet-700"
                    href={theme.preview_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={14} /> Build preview
                  </a>
                  <button
                    className="inline-flex gap-1 items-center text-slate-600"
                    onClick={() => showHistory(theme)}
                  >
                    <History size={14} /> Change history
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className="flex gap-3 items-center text-sm">
          <button
            className={button}
            disabled={!offset}
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            Previous
          </button>
          <span>
            {total ? offset + 1 : 0}–{Math.min(offset + 50, total)} of {total}
          </span>
          <button
            className={button}
            disabled={offset + 50 >= total}
            onClick={() => setOffset(offset + 50)}
          >
            Next
          </button>
        </div>
      </section>
      {history && (
        <section
          aria-label="Theme change history"
          className="bg-white border rounded-xl p-5 space-y-4"
        >
          <div className="flex justify-between items-center">
            <h2 className="font-semibold">
              {history.theme.name} · Change history
            </h2>
            <button className={button} onClick={() => setHistory(null)}>
              Close history
            </button>
          </div>
          {history.entries.map((entry) => (
            <div key={entry.id} className="border-t pt-3 text-sm">
              <p className="text-slate-500 mb-2">
                {new Date(entry.created_at).toLocaleString()} · {entry.filename}
              </p>
              {Object.entries(entry.changes).map(([k, v]) => (
                <p className="break-words" key={k}>
                  <strong>{k}</strong>: {display(v.before)} → {display(v.after)}
                </p>
              ))}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
